import { useState, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { Modal, useModalContext } from '../common/Modal';
import { ProcessModal_RetryProcessMutation, ProcessModal_RetryProcessMutationVariables, ProcessStatus } from '../../../codegen/graphql';
import { MdCheckCircle, MdError, MdSchedule, MdPlayArrow, MdRefresh, MdExpandMore } from 'react-icons/md';
import MetaTable from './MetaTable';
import dayjs from 'dayjs';
import Button from '../common/Button';
import { Tabs } from '../common/Tabs';
import { ButtonGroup } from '../common/ButtonGroup';
import Pagination from '../Pagination';
import { getStatusBadge, getStatusIcon, formatDate, getDuration } from './processesUtils';
import { useModalRouter } from '../../ModalRouter_context';


interface ProcessModalProps {
  processId: string;
}

export default function ProcessModal({ processId }: ProcessModalProps) {
  const modalRouter = useModalRouter();
  const handleClose = () => {
    modalRouter.pop();
  };

  return (
    <Modal
      title={'Process'}
      width="fullscreen"
      onClose={handleClose}
    >
      <ProcessModalContent processId={processId} />
    </Modal>
  );
}


const SUBPROCESSES_PAGE_SIZE = 10;

function ProcessModalContent({ processId }: ProcessModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'logs' | 'result' | 'subprocesses'>('details');
  const [subprocessesPage, setSubprocessesPage] = useState(1);
  const [subprocessesStatus, setSubprocessesStatus] = useState<ProcessStatus | null>(null);

  const { data, refetch } = useSuspenseQuery(GET_PROCESS, {
    variables: {
      id: processId!,
      subprocessesPage,
      subprocessesPageSize: SUBPROCESSES_PAGE_SIZE,
      subprocessesStatus: subprocessesStatus ?? undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [retryProcess] = useMutation<
    ProcessModal_RetryProcessMutation,
    ProcessModal_RetryProcessMutationVariables
  >(RETRY_PROCESS, {
    onCompleted: () => {
      refetch();
    },
  });

  const process = (data as any)?.process;

  const modalContext = useModalContext();

  useLayoutEffect(() => {
    modalContext.setTitle(
      <div className="flex items-center gap-3">
        {getStatusIcon(process.status)}
        <span>{process.name}</span>
        {getStatusBadge(process.status)}
        <span className="text-sm text-gray-500">(depth: {process.depth})</span>
      </div>
    );
  }, [process]);

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'logs', label: 'Logs', badge: process.logLines?.length || 0 },
    { id: 'result', label: process.status === 'error' ? 'Result (Error)' : 'Result' }
  ];

  if (!process) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Process not found or has been deleted.</p>
      </div>
    );
  }

  const handleRetry = (id: string) => {
    if (window.confirm('Are you sure you want to retry this process?')) {
      retryProcess({
        variables: {
          id,
          subprocessesPage,
          subprocessesPageSize: SUBPROCESSES_PAGE_SIZE,
          subprocessesStatus: subprocessesStatus ?? undefined,
        },
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 items-start">
      {process.parent && (
        <ClickableProcessTree process={process.parent} />
      )}

      {!process.parent && (
        <div className="text-center py-8 text-gray-500">
          <p>Already top level process</p>
        </div>
      )}
      <div className="flex gap-4 self-stretch">
        <div className="space-y-2 border rounded-lg p-4 border-gray-400">
          <h3 className="text-lg font-medium text-gray-900">Sub-processes ({process.directSubProcesses.total})</h3>
          <Pagination
            currentPage={subprocessesPage}
            totalPages={Math.ceil(process.directSubProcesses.total / SUBPROCESSES_PAGE_SIZE)}
            totalItems={process.directSubProcesses.total}
            pageSize={SUBPROCESSES_PAGE_SIZE}
            hasNextPage={subprocessesPage < Math.ceil(process.directSubProcesses.total / SUBPROCESSES_PAGE_SIZE)}
            onPageChange={setSubprocessesPage}
            padded={false}
          />
          <ButtonGroup<ProcessStatus | 'all'>
            label="Filter by status"
            options={[
              { value: 'all', label: 'All' },
              { value: ProcessStatus.Pending, label: 'Pending' },
              { value: ProcessStatus.Started, label: 'Started' },
              { value: ProcessStatus.Partial, label: 'Partial' },
              { value: ProcessStatus.Success, label: 'Success' },
              { value: ProcessStatus.Error, label: 'Error' },
            ]}
            value={subprocessesStatus ?? 'all'}
            onChange={(v) => {
              setSubprocessesStatus(v === 'all' ? null : (v as ProcessStatus));
              setSubprocessesPage(1);
            }}
          />
          {process.directSubProcesses.nodes.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No sub-processes found for this process.</p>
            </div>
          )}

          {process.directSubProcesses.nodes.map((subProcess: any) => (
            <ClickableProcessTree key={subProcess.id} process={subProcess} />
          ))}
        </div>

        <div className="flex-1">
          {/* Tab Navigation */}
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tabId) => setActiveTab(tabId as any)}
          />

          <div className="border border-t-0 rounded-tl-none rounded-tr-none rounded-lg p-4 border-gray-400">
            {/* Tab Content */}
            {activeTab === 'details' && (
              <>
              {console.log('status', process.status, process.status === 'error' || process.status === 'partial')}
                {(process.status === 'error' || process.status === 'partial') && (
                  <Button
                    onClick={() => handleRetry(process.id)}
                    preset="primary"
                    size="sm"
                  >
                    <MdRefresh size={18} /> Retry failed processes
                  </Button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-md font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-md text-gray-900">{process.name}</p>
                  </div>
                  <div>
                    <label className="block text-md font-medium text-gray-700">Status</label>
                    <div className="mt-1">{getStatusBadge(process.status)}</div>
                  </div>
                  <div>
                    <label className="block text-md font-medium text-gray-700">Function Name</label>
                    <p className="mt-1 text-md text-gray-900 font-mono">{process.functionName || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-md font-medium text-gray-700">Duration</label>
                    <p className="mt-1 text-md text-gray-900">{getDuration(process.startedAt, process.completedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-md font-medium text-gray-700">Started At</label>
                    <p className="mt-1 text-md text-gray-900">{formatDate(process.startedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-md font-medium text-gray-700">Completed At</label>
                    <p className="mt-1 text-md text-gray-900">{formatDate(process.completedAt)}</p>
                  </div>
                </div>

                {process.description && (
                  <div>
                    <label className="block text-md font-medium text-gray-700">Description</label>
                    <p className="mt-1 text-md text-gray-900">{process.description}</p>
                  </div>
                )}

                {process.arguments && (
                  <MetaTable meta={process.arguments} title="Arguments" />
                )}
              </>
            )}

            {activeTab === 'logs' && (
              <>
                {process.logLines
                  && process.logLines.length > 0
                  && process.logLines.map((log: any, index: number) => (
                    <LogLine key={index} log={log} />
                  ))
                }
              </>
            )}

            {activeTab === 'result' && (
              <>
                <div className="bg-gray-100 rounded-lg p-4">
                  <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                    {process.result
                      ? JSON.stringify(process.result, null, 2)
                      : 'No result available'}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>

      </div>
  </div>);
}

function ClickableProcessTree({ process }: { process: any }) {
  const modalRouter = useModalRouter();

  return (
    <div
      key={process.id}
      onClick={() => modalRouter.push({ s: 'processModal', a: { processId: process.id } })}
      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 cursor-pointer transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {getStatusIcon(process.status)}
            <h4 className="text-sm font-medium text-gray-900">{process.name}</h4>
            {getStatusBadge(process.status)}
          </div>
          {process.description && (
            <p className="text-sm text-gray-600 mb-1">{process.description}</p>
          )}
          {process.functionName && (
            <p className="text-xs text-gray-500 font-mono">{process.functionName}</p>
          )}
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>Created: {formatDate(process.createdAt)}</div>
          {process.startedAt && (
            <div>Started: {formatDate(process.startedAt)}</div>
          )}
          {process.completedAt && (
            <div>Completed: {formatDate(process.completedAt)}</div>
          )}
          <div className="mt-1">
            Duration: {getDuration(process.startedAt, process.completedAt)}
          </div>
        </div>
      </div>
    </div>
  );
}


function LogLine({ log }: { log: any }) {
  const {
    message,
    level,
    timestamp,
    ms,
    ...meta
  } = log;

  const [metaExpanded, setMetaExpanded] = useState(false);

  return (
    <div
      className={`p-3 rounded-lg border ${getLogTypeColor(level)}`}
    >
      <div className="flex flex-row space-between items-center">
        <span className="text-sm text-gray-500">
          {dayjs(timestamp).format('HH:mm:ss')} / {ms}
        </span>
        <div className="flex-1" />
        <Button
          onClick={() => setMetaExpanded(!metaExpanded)}
          size="sm"
          preset="white"
        >
          {metaExpanded ? 'Hide Meta' : 'Show Meta'}
          <MdExpandMore size={20} />
        </Button>
      </div>

      <p className="text-lg">
        {message}
      </p>

      {metaExpanded && (
        <MetaTable meta={meta} />
      )}
    </div>
  )
}

const getLogTypeColor = (type: string) => {
  switch (type) {
    case 'error':
      return 'text-red-600 bg-red-50';
    case 'warn':
      return 'text-yellow-600 bg-yellow-50';
    case 'info':
      return 'text-blue-600 bg-blue-50';
    case 'success':
      return 'text-green-600 bg-green-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};


const PROCESS_FRAGMENT = gql`
  fragment ProcessModal_Process on Process {
    id
    name
    description
    status
    arguments
    functionName
    logLines
    result
    startedAt
    completedAt
    createdAt
    updatedAt
    depth
    parent {
      id
      name
      status
    }
    root {
      id
      name
      status
    }
    directSubProcesses(page: $subprocessesPage, pageSize: $subprocessesPageSize, status: $subprocessesStatus) {
      nodes {
        id
        name
        description
        status
        functionName
        startedAt
        completedAt
        createdAt
      }
      total
    }
  }
`;

const GET_PROCESS = gql`
  query ProcessModal_GetProcess($id: ID!, $subprocessesPage: Int, $subprocessesPageSize: Int, $subprocessesStatus: ProcessStatus) {
    process(id: $id) {
      ...ProcessModal_Process
    }
  }
  ${PROCESS_FRAGMENT}
`;

const RETRY_PROCESS = gql`
  mutation ProcessModal_RetryProcess($id: ID!, $subprocessesPage: Int, $subprocessesPageSize: Int, $subprocessesStatus: ProcessStatus) {
    retryProcess(id: $id) {
      ...ProcessModal_Process
    }
  }
  ${PROCESS_FRAGMENT}
`;